import { useEffect, useMemo, useState } from 'react';
import type { PackagePricingResult } from '@growthdept/blocks/gift-pricing';
import { fetchLatestConfirmation } from '@/lib/api/confirmations';
import {
  buildPricingConversionPreview,
  convertPackagePricing,
  fetchLatestPricingConversion,
  revertPackagePricing,
  serviceMarginDefaults,
  type PricingConversionAudit,
} from '@/lib/pricingConversion';
import type { GiftPackage } from '@/lib/types';

interface Props {
  pkg: GiftPackage;
  onChanged: () => void | Promise<void>;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) return String(error.message);
  return 'De prijsmodelwijziging is niet opgeslagen. Probeer het opnieuw.';
}

function validMargin(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value < 100;
}

export function PricingModelConversion({ pkg, onChanged }: Props) {
  const defaults = serviceMarginDefaults(pkg);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [handlingMargin, setHandlingMargin] = useState(defaults.handling);
  const [transportMargin, setTransportMargin] = useState(defaults.transport);
  const [audit, setAudit] = useState<PricingConversionAudit | null>(null);
  const [rollbackLocked, setRollbackLocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setPreviewOpen(false);
    setHandlingMargin(serviceMarginDefaults(pkg).handling);
    setTransportMargin(serviceMarginDefaults(pkg).transport);
    setError(null);
  }, [pkg.id, pkg.pricing_model_version]);

  useEffect(() => {
    let active = true;
    if (pkg.pricing_model_version !== 'component_margin_v2') {
      setAudit(null);
      setRollbackLocked(false);
      return;
    }
    Promise.all([
      fetchLatestPricingConversion(pkg.id),
      pkg.project_id ? fetchLatestConfirmation(pkg.project_id) : Promise.resolve(null),
    ])
      .then(([latestAudit, latestConfirmation]) => {
        if (!active) return;
        setAudit(latestAudit);
        setRollbackLocked(
          Boolean(
            latestAudit &&
              latestConfirmation &&
              new Date(latestConfirmation.sent_at).getTime() >
                new Date(latestAudit.converted_at).getTime()
          )
        );
      })
      .catch((loadError) => {
        if (active) setError(errorMessage(loadError));
      });
    return () => {
      active = false;
    };
  }, [pkg.id, pkg.project_id, pkg.pricing_model_version]);

  const marginsValid = validMargin(handlingMargin) && validMargin(transportMargin);
  const preview = useMemo(
    () =>
      marginsValid
        ? buildPricingConversionPreview(pkg, handlingMargin, transportMargin)
        : null,
    [pkg, handlingMargin, transportMargin, marginsValid]
  );

  async function convert() {
    if (!preview) {
      setError('Vul voor handling en transport een marge van 0% tot lager dan 100% in.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await convertPackagePricing(
        pkg.id,
        handlingMargin,
        transportMargin,
        { ...preview.before.total },
        { ...preview.after.total }
      );
      setPreviewOpen(false);
      await onChanged();
    } catch (convertError) {
      setError(errorMessage(convertError));
    } finally {
      setBusy(false);
    }
  }

  async function revert() {
    setBusy(true);
    setError(null);
    try {
      await revertPackagePricing(pkg.id);
      await onChanged();
    } catch (revertError) {
      setError(errorMessage(revertError));
    } finally {
      setBusy(false);
    }
  }

  if (pkg.pricing_model_version === 'component_margin_v2') {
    const canRevert = audit && !audit.reverted_at && !rollbackLocked;
    return (
      <section className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 text-xs dark:border-emerald-500/30 dark:bg-emerald-500/10">
        <div className="font-semibold text-emerald-800 dark:text-emerald-200">
          Nieuw rekenmodel — vier brutomarges
        </div>
        <p className="mt-1 text-emerald-700 dark:text-emerald-300">
          Artikelen 9%, artikelen 21%, handling en transport worden afzonderlijk berekend.
        </p>
        {canRevert && (
          <button
            type="button"
            disabled={busy}
            onClick={revert}
            className="mt-2 rounded-md border border-emerald-300 bg-white px-3 py-1.5 font-medium text-emerald-800 disabled:opacity-50 dark:border-emerald-500/50 dark:bg-bmade-900 dark:text-emerald-200"
          >
            {busy ? 'Terugdraaien...' : 'Terug naar beschermd rekenmodel'}
          </button>
        )}
        {rollbackLocked && (
          <p className="mt-2 text-amber-700 dark:text-amber-300">
            Terugdraaien is vergrendeld omdat na de omzetting een orderbevestiging is verstuurd.
          </p>
        )}
        {error && <p role="alert" className="mt-2 text-red-700 dark:text-red-300">{error}</p>}
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-xs dark:border-amber-500/30 dark:bg-amber-500/10">
      <div className="font-semibold text-amber-900 dark:text-amber-200">
        Huidig rekenmodel — beschermd
      </div>
      <p className="mt-1 text-amber-800 dark:text-amber-300">
        De bestaande prijs blijft ongewijzigd totdat je de vergelijking controleert en bevestigt.
      </p>
      {!previewOpen ? (
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          className="mt-2 rounded-md bg-amber-700 px-3 py-1.5 font-medium text-white hover:bg-amber-800"
        >
          Nieuw rekenmodel bekijken
        </button>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {preview && (
              <>
                <PreviewCard title="Huidige aanpak" pricing={preview.before} quantity={pkg.quantity} />
                <PreviewCard title="Nieuwe aanpak" pricing={preview.after} quantity={pkg.quantity} />
              </>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <MarginInput
              label="Brutomarge handling"
              value={handlingMargin}
              onChange={setHandlingMargin}
            />
            <MarginInput
              label="Brutomarge transport"
              value={transportMargin}
              onChange={setTransportMargin}
            />
          </div>
          {!marginsValid && (
            <p role="alert" className="text-red-700 dark:text-red-300">
              Vul voor handling en transport een marge van 0% tot lager dan 100% in.
            </p>
          )}
          {preview && (
            <div className="rounded-md bg-white/70 p-2 text-bmade-700 dark:bg-bmade-900/50 dark:text-bmade-300">
              Verschil incl. btw:{' '}
              <strong>
                EUR {(preview.after.total.selling_incl_btw - preview.before.total.selling_incl_btw).toFixed(2)}
              </strong>{' '}
              ({percentageDifference(
                preview.before.total.selling_incl_btw,
                preview.after.total.selling_incl_btw
              )})
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || !preview}
              onClick={convert}
              className="rounded-md bg-accent-600 px-3 py-1.5 font-semibold text-white disabled:opacity-50"
            >
              {busy ? 'Omzetten...' : 'Definitief omzetten naar nieuw rekenmodel'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setPreviewOpen(false)}
              className="rounded-md border border-bmade-300 bg-white px-3 py-1.5 text-bmade-700 disabled:opacity-50 dark:border-bmade-700 dark:bg-bmade-900 dark:text-bmade-200"
            >
              Annuleren
            </button>
          </div>
          {error && <p role="alert" className="text-red-700 dark:text-red-300">{error}</p>}
        </div>
      )}
    </section>
  );
}

function PreviewCard({
  title,
  pricing,
  quantity,
}: {
  title: string;
  pricing: PackagePricingResult;
  quantity: number;
}) {
  const total = pricing.total;
  return (
    <div className="rounded-md border border-bmade-200 bg-white p-3 dark:border-bmade-700 dark:bg-bmade-900">
      <div className="font-semibold text-bmade-950 dark:text-white">{title}</div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-bmade-600 dark:text-bmade-300">
        <dt>Kostprijs</dt><dd className="text-right">EUR {total.cost.toFixed(2)}</dd>
        <dt>Verkoop excl.</dt><dd className="text-right">EUR {total.selling_excl_btw.toFixed(2)}</dd>
        <dt>BTW</dt><dd className="text-right">EUR {total.btw_euros.toFixed(2)}</dd>
        <dt>Verkoop incl.</dt><dd className="text-right">EUR {total.selling_incl_btw.toFixed(2)}</dd>
        <dt>Per pakket</dt>
        <dd className="text-right">EUR {(total.selling_incl_btw / Math.max(1, quantity)).toFixed(2)}</dd>
      </dl>
    </div>
  );
}

function MarginInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 text-bmade-700 dark:text-bmade-300">
      <span>{label}</span>
      <span className="flex items-center gap-1">
        <input
          aria-label={label}
          type="number"
          min="0"
          max="99.99"
          step="0.5"
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
          className="w-20 rounded-md border border-bmade-300 bg-white px-2 py-1 text-right text-bmade-950 dark:border-bmade-700 dark:bg-bmade-900 dark:text-white"
        />
        %
      </span>
    </label>
  );
}

function percentageDifference(before: number, after: number): string {
  if (before === 0) return after === 0 ? '0,0%' : 'geen vergelijkingsbasis';
  return `${(((after - before) / before) * 100).toFixed(1).replace('.', ',')}%`;
}
